import React, { useState, useEffect, useCallback } from 'react'
import { publicFormActivityAPI, platformAPI } from '../services/api'
import {
  ClipboardList,
  Search,
  RefreshCw,
  AlertCircle,
  ShieldAlert,
  Loader2,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  UserPlus,
} from 'lucide-react'

// Mirrors ALL_OUTCOMES in vs-main-api/models/public_form_activity.py.
const OUTCOMES = [
  { key: 'client_created', label: 'Cliente criado', tone: 'green' },
  { key: 'client_matched', label: 'Cliente existente', tone: 'green' },
  { key: 'in_progress', label: 'Em andamento', tone: 'blue' },
  { key: 'spam_flagged', label: 'Marcado como spam', tone: 'amber' },
  { key: 'spam_discarded', label: 'Spam descartado', tone: 'red' },
  { key: 'validation_error', label: 'Erro de validação', tone: 'red' },
  { key: 'server_error', label: 'Erro no servidor', tone: 'red' },
  { key: 'link_unavailable', label: 'Link indisponível', tone: 'gray' },
  { key: 'viewed', label: 'Visualizado', tone: 'gray' },
]

const OUTCOME_BY_KEY = Object.fromEntries(OUTCOMES.map((o) => [o.key, o]))

const TONE_CLASSES = {
  green: 'bg-green-100 text-green-800',
  blue: 'bg-blue-100 text-blue-800',
  amber: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-800',
  gray: 'bg-admin-100 text-admin-700',
}

// The tiles that answer "is anything being lost right now?".
const STAT_TILES = [
  { key: 'client_created', label: 'Clientes criados', icon: UserPlus, tone: 'green' },
  { key: 'client_matched', label: 'Clientes existentes', icon: CheckCircle2, tone: 'green' },
  { key: 'in_progress', label: 'Em andamento', icon: Loader2, tone: 'blue' },
  { key: 'spam_flagged', label: 'Marcados como spam', icon: ShieldAlert, tone: 'amber' },
  { key: 'spam_discarded', label: 'Spam descartado', icon: ShieldAlert, tone: 'red' },
  { key: 'server_error', label: 'Erros no servidor', icon: AlertCircle, tone: 'red' },
]

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—')

const OutcomeBadge = ({ outcome }) => {
  const meta = OUTCOME_BY_KEY[outcome] || { label: outcome, tone: 'gray' }
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${TONE_CLASSES[meta.tone]}`}
    >
      {meta.label}
    </span>
  )
}

const ProgressCell = ({ row }) => {
  if (row.total_questions === null || row.total_questions === undefined) {
    return <span className="text-admin-400">—</span>
  }
  const percent = row.progress_percent ?? 0
  return (
    <div className="min-w-[8rem]">
      <div className="flex items-center justify-between text-xs text-admin-600 mb-1">
        <span>
          {row.answered_count}/{row.total_questions}
        </span>
        {/* Answered everything but never pressed Submit is the abandonment signal. */}
        <span className={row.completed ? 'text-green-700' : 'text-admin-400'}>
          {row.completed ? 'enviado' : 'não enviado'}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-admin-100">
        <div
          className={`h-1.5 rounded-full ${row.completed ? 'bg-green-500' : 'bg-admin-400'}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  )
}

const DetailRow = ({ label, value }) => (
  <div>
    <dt className="text-xs font-medium text-admin-500">{label}</dt>
    <dd className="text-sm text-admin-900 break-all">{value || '—'}</dd>
  </div>
)

const ActivityDetail = ({ activityId }) => {
  const [detail, setDetail] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    publicFormActivityAPI
      .getOne(activityId)
      .then((res) => {
        if (!cancelled) setDetail(res.data.data)
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error?.message || 'Falha ao carregar os detalhes')
      })
    return () => {
      cancelled = true
    }
  }, [activityId])

  if (error) {
    return <div className="p-4 text-sm text-red-700">{error}</div>
  }
  if (!detail) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-admin-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
      </div>
    )
  }

  return (
    <div className="bg-admin-50 p-4 space-y-4">
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <DetailRow label="Sessão" value={detail.session_id} />
        <DetailRow label="UUID do formulário" value={detail.public_uuid} />
        <DetailRow label="ID do cliente" value={detail.client_id} />
        <DetailRow label="Última etapa" value={detail.last_step} />
        <DetailRow label="IP" value={detail.ip} />
        <DetailRow label="Referer" value={detail.referer} />
        <DetailRow label="Primeiro acesso" value={formatDate(detail.first_seen_at)} />
        <DetailRow label="Requisições" value={`${detail.request_count} (${detail.autosave_count} salvamento automático, ${detail.submit_count} envio)`} />
      </dl>

      <DetailRow label="User agent" value={detail.user_agent} />

      {detail.dropped_answer_count > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
          {detail.dropped_answer_count} resposta(s) foram enviadas para perguntas que não
          pertencem a este questionário e foram descartadas pela API.
        </div>
      )}

      {detail.error_detail && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3">
          <div className="text-xs font-medium text-red-700 mb-1">Erro</div>
          <div className="text-sm text-red-900 break-all">{detail.error_detail}</div>
        </div>
      )}

      <div>
        <div className="text-xs font-medium text-admin-500 mb-2">
          Sinais de spam (pontuação {detail.spam_score})
        </div>
        {detail.spam_signals?.length ? (
          <div className="flex flex-wrap gap-2">
            {detail.spam_signals.map(([name, points]) => (
              <span
                key={name}
                className="inline-flex items-center rounded-full bg-admin-200 px-2 py-0.5 text-xs text-admin-800"
              >
                {name} +{points}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-sm text-admin-500">Nenhum sinal acionado</span>
        )}
      </div>
    </div>
  )
}

export const PublicFormActivityPage = () => {
  const [rows, setRows] = useState([])
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
  const [stats, setStats] = useState(null)
  const [tenants, setTenants] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [expandedId, setExpandedId] = useState(null)

  const [search, setSearch] = useState('')
  const [outcome, setOutcome] = useState('')
  const [tenantId, setTenantId] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  useEffect(() => {
    platformAPI
      .getTenants()
      .then((res) => setTenants(res.data.data || res.data.tenants || []))
      .catch(() => setTenants([]))
  }, [])

  const buildParams = useCallback(() => {
    const params = {}
    if (search.trim()) params.q = search.trim()
    if (outcome) params.outcome = outcome
    if (tenantId) params.tenant_id = tenantId
    if (dateFrom) params.date_from = dateFrom
    if (dateTo) params.date_to = dateTo
    return params
  }, [search, outcome, tenantId, dateFrom, dateTo])

  const loadRows = useCallback(
    async (page = 1) => {
      setLoading(true)
      setError('')
      try {
        const res = await publicFormActivityAPI.list({ ...buildParams(), page, per_page: 50 })
        setRows(res.data.data || [])
        setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 })
      } catch (err) {
        setRows([])
        setError(err.response?.data?.error?.message || 'Falha ao carregar a atividade dos formulários')
      } finally {
        setLoading(false)
      }
    },
    [buildParams]
  )

  const loadStats = useCallback(async () => {
    try {
      const params = { days: 30 }
      if (tenantId) params.tenant_id = tenantId
      const res = await publicFormActivityAPI.stats(params)
      setStats(res.data.data)
    } catch {
      setStats(null)
    }
  }, [tenantId])

  useEffect(() => {
    loadRows(1)
  }, [loadRows])

  useEffect(() => {
    loadStats()
  }, [loadStats])

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      loadRows(newPage)
    }
  }

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    loadRows(1)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-900">Formulários públicos</h1>
        <p className="text-admin-600 mt-1">
          Todas as sessões de questionários públicos, inclusive as que nunca viraram cliente.
          Pesquise por e-mail, nome ou telefone para rastrear um lead específico.
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {STAT_TILES.map((tile) => {
            const Icon = tile.icon
            return (
              <div key={tile.key} className="card p-4">
                <div className="flex items-center gap-2 text-admin-500 mb-1">
                  <Icon className="h-4 w-4" />
                  <span className="text-xs font-medium">{tile.label}</span>
                </div>
                <div className="text-2xl font-bold text-admin-900">
                  {stats.by_outcome?.[tile.key] ?? 0}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <form onSubmit={handleSearchSubmit} className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-admin-700 mb-2" htmlFor="pfa-search">
              Pesquisar
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-admin-400" />
              <input
                id="pfa-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="E-mail, nome, telefone ou sessão"
                className="block w-full rounded-lg border border-admin-300 pl-9 pr-3 py-2 text-sm text-admin-900 focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-admin-700 mb-2" htmlFor="pfa-outcome">
              Resultado
            </label>
            <select
              id="pfa-outcome"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              className="block w-full rounded-lg border border-admin-300 px-3 py-2 text-sm text-admin-900 focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">Todos os resultados</option>
              {OUTCOMES.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-admin-700 mb-2" htmlFor="pfa-tenant">
              Estúdio
            </label>
            <select
              id="pfa-tenant"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className="block w-full rounded-lg border border-admin-300 px-3 py-2 text-sm text-admin-900 focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">Todos os estúdios</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-admin-700 mb-2" htmlFor="pfa-from">
              De
            </label>
            <input
              id="pfa-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="block w-full rounded-lg border border-admin-300 px-3 py-2 text-sm text-admin-900 focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-admin-700 mb-2" htmlFor="pfa-to">
              Até
            </label>
            <input
              id="pfa-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="block w-full rounded-lg border border-admin-300 px-3 py-2 text-sm text-admin-900 focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <button
            type="submit"
            className="btn btn-secondary flex items-center gap-1.5"
            aria-label="Atualizar atividade dos formulários"
            tabIndex={0}
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </form>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800 flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Results */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 p-12 text-admin-500">
            <Loader2 className="h-5 w-5 animate-spin" /> Carregando…
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-12 text-admin-500">
            <ClipboardList className="h-8 w-8 text-admin-300" />
            <span className="text-sm">Nenhuma atividade de formulário corresponde a estes filtros</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-admin-200">
              <thead className="bg-admin-50">
                <tr>
                  {['Quando', 'Estúdio', 'Questionário', 'Contato', 'Progresso', 'Resultado', 'Spam', ''].map(
                    (header) => (
                      <th
                        key={header}
                        className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider"
                      >
                        {header}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-admin-200 bg-white">
                {rows.map((row) => (
                  <React.Fragment key={row.id}>
                    <tr className="hover:bg-admin-50">
                      <td className="px-4 py-3 text-sm text-admin-600 whitespace-nowrap">
                        {formatDate(row.last_seen_at)}
                      </td>
                      <td className="px-4 py-3 text-sm text-admin-900">{row.tenant_name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-admin-600">
                        {row.questionnaire_name || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="text-admin-900">{row.name || '—'}</div>
                        <div className="text-admin-500 text-xs">{row.email || row.phone || '—'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <ProgressCell row={row} />
                      </td>
                      <td className="px-4 py-3">
                        <OutcomeBadge outcome={row.outcome} />
                      </td>
                      <td className="px-4 py-3 text-sm text-admin-600">
                        {row.spam_score > 0 ? row.spam_score : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                          className="text-admin-500 hover:text-admin-800"
                          aria-label={expandedId === row.id ? 'Ocultar detalhes' : 'Mostrar detalhes'}
                          tabIndex={0}
                        >
                          {expandedId === row.id ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </button>
                      </td>
                    </tr>
                    {expandedId === row.id && (
                      <tr>
                        <td colSpan={8} className="p-0">
                          <ActivityDetail activityId={row.id} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination.pages > 1 && (
          <div className="flex items-center justify-between border-t border-admin-200 px-4 py-3">
            <span className="text-sm text-admin-600">
              Página {pagination.page} de {pagination.pages} · {pagination.total} sessões
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="btn btn-secondary disabled:opacity-50"
                aria-label="Página anterior"
                tabIndex={0}
              >
                Anterior
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="btn btn-secondary disabled:opacity-50"
                aria-label="Próxima página"
                tabIndex={0}
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PublicFormActivityPage
